import {Event, PREVIEW_ID_ATTRIBUTE} from './lib/_consts.js';
import {$, $EventEmitter} from './lib/$utils.js';
import Messenger from './lib/_messenger.js';
import Actions from './lib/actions.js';
import Decoration from './lib/decoration.js';

import DefaultButtons from './lib/_default-buttons.js';
import DragAndDrop from './lib/_dnd.js';
import DisplayChanges from './lib/_display-change-set.js';

import '../common/fs-tpp.css';

/**
 * Global FirstSpiritPreview instance.
 *
 * @namespace TPP_SNAP
 *
 * @example
 * <!--
 *   The fs-tpp-api/snap.js must be used in the markup of the third party app. There is no specific
 *   position defined, but if you want to use the API of window.TPP_SNAP, your own script
 *   must be loaded after the inclusion fs-tpp-api/snap.js, of course.
 *   You could define the optional [data-firstspirit-origin] attribute to ensure that the
 *   first(!) postMessage (aka. TPP handshake) is only committed to the real ContentCreator frame.
 * -->
 * <script src="path/to/fs-tpp-api/snap.js" data-firstspirit-origin="http://firstspirit:8000"></script>
 *
 * @example
 * <!--
 *   All you have to do is use the [data-preview-id] in your markup. This library
 *   automatically decorates those containers with the known borders and buttons. Content
 *   changes will force a refresh of the current page, so there is no need to implement in JS!
 *
 *   But of course you are able to do this by using the framework! That's what the docs are for...
 *
 *   As an example, a FirstSpirit Template delivers some HTML Markup, which you would like to update
 *   in-place in case of changes. You could use the [data-on-tpp-change] attribute which is
 *   a representation of the {@link onContentChange~Handler}. Or you use it directly, like this:
 *
 *   TPP_SNAP.onContentChange(($node, previewId, content) => {
 *     if ($node.matches('.content') && content !== null) {
 *       $node.innerHTML = content;
 *       return false;
 *     }
 *   })
 *
 *   You can register multiple handlers. Further processing of the event is stopped after the first
 *   handler returns a value !=== undefined.
 * -->
 * <div class="content"
 *   data-preview-id="<previewId>"
 *   data-on-tpp-change="if (content !== null) { this.innerHTML = content; return false; }">
 * </div>
 */
class FirstSpiritPreview {
  constructor({ actions, connected }) {
    this._actions = actions;
    this._connected = connected;
    this._buttons = [];
    this._listener = [
      ['onContentChangeFallback', async ($node, previewId, content) => {
        const event = new CustomEvent('tpp-update', { detail: { previewId, content }, bubbles: false, cancelable: true });
        if (!$node) { // content change on previewId which is not directly in DOM (e.g. the (CC-)PreviewElement)
          const cancelled = !document.body.dispatchEvent(event);
          return cancelled ? false : undefined;
        }
        const cancelled = !$node.dispatchEvent(event);
        if (cancelled) return false;

        if ('onTppUpdate' in $node.dataset) {
          // via attribute [data-on-tpp-update]
          return await (new Function('previewId', 'content', $node.dataset.onTppUpdate)).call($node, previewId, content);

        } else if (content === null) {
          // node is deleted
          if (previewId === await this.getPreviewElement()) {
            return location.href = await this.previewUrl();
          }
          const $parent = $node.parentNode;
          $node.parentNode.removeChild($node);
          return $parent.querySelector('[data-preview-id]') || undefined;

        } else if (typeof content === 'string') {
          // if result is html markup try to replace
          const fragment = document.createDocumentFragment();
          fragment.appendChild(document.createElement('div')).innerHTML = content;
          const nodes = [...fragment.firstChild.children];
          if (nodes.length === 1 && nodes[0].matches(`[${PREVIEW_ID_ATTRIBUTE}="${previewId}"]`)) {
            $node.outerHTML = nodes[0].outerHTML;
            return false;
          }
        }
      }],
      ['onRerenderViewFallback', () => {
        location.reload()
      }]
    ];
  }

  /**
   * Will be true, if the TPP handshake was successful.
   * @memberof TPP_SNAP
   * @property {Promise<boolean>} isConnected
   *
   * @since 1.2.0
   */
  get isConnected() {
    return this._connected;
  }

  /**
   * @callback onInit~Handler
   * @param {boolean} success - the TPP handshake was successful or not
   *
   * @since 1.2.0
   */
  /**
   * Will be called after internal initialization (even if the handler is set
   * after initialization).
   *
   * @memberof TPP_SNAP
   * @method onInit
   * @param {onInit~Handler} handler
   *
   * @since 1.2.0
   *
   * @example TPP_SNAP.onInit(async (success) => console.log(`FirstSpirit Preview is ${success ? 'now' : 'NOT'} available!`))
   */
  onInit(handler) {
    this._connected.then(handler);
  }

  /**
   * @callback onContentChange~Handler
   * @param {HTMLElement} $node - a DOM node which contains the [data-preview-id] attribute
   * @param {string} previewId - the current PreviewId
   * @param {any} content - the changed content, could be a string,
   *   or an object (if the FirstSpirit Template generates JSON) or null, if
   *   this PreviewElement was deleted.
   * @return - if the handler doesn't return anything, {@link onRerenderView~Handler} will be triggered.
   *
   * @since 1.2.0
   */
  /**
   * Is triggered any time the content changes (or is deleted), on each Node that
   * was associated by the `[data-preview-id]` attribute in the markup.
   *
   * @memberof TPP_SNAP
   * @method onContentChange
   * @param {onContentChange~Handler} handler
   *
   * @since 1.2.0
   *
   * @example TPP_SNAP.onContentChange(($node, previewId, content) => $node.innerHTML = content))
   */
  onContentChange(handler) {
    this._listener.push(['onContentChange', handler]);
  }

  /**
   * @callback onRerenderView~Handler
   */
  /**
   * Sometimes it's _easier_ to rerender the complete view instead of implementing
   * event handlers for anything that could happen on the FristSpirit side.
   * Additional this is the common fallback, if the {@link onContentChange~Handler}
   * is not handled by the frontend application.
   *
   * If there is no listener defined, there is a fallback to **`location.reload()`**!
   *
   * @memberof TPP_SNAP
   * @method onRerenderView
   * @param {onRerenderView~Handler} handler
   *
   * @since 1.2.0
   *
   * @example
   * TPP_SNAP.onRerenderView(() => app.rerender());
   */
  onRerenderView(handler) {
    this._listener.push(['onRerenderView', handler]);
  }

  /**
   * @callback onNavigationChange~Handler
   * @param {string|null} previewId - of a newly created page, otherwise null
   *
   * @since 1.2.0
   */
  /**
   * Creates an event if the site's structure has been changed.
   *
   * See also {@link https://docs.e-spirit.com/odfs52/dev/index.html?de/espirit/firstspirit/webedit/client/api/Common.NavigationChangeListener.html|WE_API.Common.NavigationChangeListener}.
   *
   * @memberof TPP_SNAP
   * @method onNavigationChange
   * @param {onNavigationChange~Handler} handler
   *
   * @since 1.2.0
   */
  onNavigationChange(fn) {
    this._listener.push(['onNavigationChange', fn]);
  }

  /**
   * @callback onRequestPreviewElement~Handler
   * @param {string} previewId - the current PreviewId
   *
   * @since 1.2.0
   */
  /**
   * If the user navigates via the ContentCreator Frame, e.g. by clicking on a search
   * result, this handler is called. The FrontEnd application should now _translate_
   * the _PreviewId_ to a route, and follow it.
   *
   * See also {@link https://docs.e-spirit.com/odfs52/dev/index.html?de/espirit/firstspirit/webedit/client/api/Common.PreviewRequestHandler.html|WE_API.Common.PreviewRequestHandler}.
   *
   * @memberof TPP_SNAP
   * @method onRequestPreviewElement
   * @param {onRequestPreviewElement~Handler} handler
   *
   * @since 1.2.0
   *
   * @example
   * TPP_SNAP.onRequestPreviewElement(async (previewId) => {
   *   const content = await TPP_SNAP.renderElement(previewId);
   *   $('body').innerHTML = content;
   * })
   */
  onRequestPreviewElement(fn) {
    this._listener.push(['onRequestPreviewElement', fn]);
  }

  /**
   * @name Button
   * @param {object} button
   * @param {string} [button.label = ''] - simple labeling, used by the default of {@link Button#getLabel~Callback}
   * @param {string} [button.css] - simple css class definition, used by the default of {@link Button#getIcon~Callback}
   * @param {string} [button.icon] - simple icon url, used by the default of {@link Button#getIcon~Callback}
   * @param {Button#isVisible~Callback} [button.isVisible] - whether this button should be rendered or not; the default is `(scope) => true`
   * @param {Button#isEnabled~Callback} [button.isEnabled] - whether this button should be enabled or not; the default is `(scope) => false`
   * @param {Button#getIcon~Callback} [button.getIcon] - use `scope.$button` to define the appearance of the button
   * @param {Button#getLabel~Callback} [button.getLabel] - the tooltip (`[title]`) for this button
   * @param {Button#getItems~Callback} [button.getItems] - if this is not an empty list, a dropdown will be rendered; the default is `(scope) => []`
   * @param {Button#beforeExecute~Callback} [button.beforeExecute] - will be called before {@link Button#execute~Callback}
   * @param {Button#execute~Callback} [button.execute] - will be called, when the button (or an item) is clicked
   * @param {Button#afterExecute~Callback} [button.afterExecute] - will be called after {@link Button#execute~Callback}
   */

  /**
   * The _ButtonScope_ is an object which is used by all button callbacks.
   *
   * @name ButtonScope
   * @param {HTMLElement} $node - the DOM node where the decoration appears
   * @param {HTMLElement} $button - the DOM node of the button (not available in {@link Button#isVisible~Callback})
   * @param {string} previewId - the PreviewId
   * @param {Status} status - the current {@link Status} object of PreviewId
   * @param {string} language - the current language
   */

  /**
   * Be careful with your Promise here: the rendering of **all** buttons only happens after all `Button#isVisible` calls.
   * That's why `ButtonScope.$button` doesn't exists in {@link ButtonScope} this time.
   *
   * @callback Button#isVisible~Callback
   * @param {ButtonScope} scope
   * @return {Promise<boolean>}
   * @since 1.2.0
   */
  /**
   * @callback Button#isEnabled~Callback
   * @param {ButtonScope} scope
   * @return {Promise<boolean>}
   * @since 1.2.0
   */
  /**
   * @callback Button#getIcon~Callback
   * @param {ButtonScope} scope
   * @since 1.2.0
   * @example
   * // the default callback is defined as:
   * TPP_SNAP.registerButton({
   *   icon = null,
   *   css = null,
   *   getIcon = async ({ $button }) =>
   *     (css !== null && !$button.classList.add(css))
   *     || (icon !== null && ($button.style.backgroundImage = `url(${icon})`))
   *     || $button.classList.add('tpp-icon-action'),
   *   ...
   * });
   */
  /**
   * @callback Button#getLabel~Callback
   * @param {ButtonScope} scope
   * @since 1.2.0
   * @example
   * // the default callback is defined as:
   * TPP_SNAP.registerButton({
   *   label = '',
   *   getLabel = () => label,
   *   ...
   * });
   *
   * @example
   * // localize
   * TPP_SNAP.registerButton({
   *   getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Deutsche Bezeichnung' : 'English Label',
   *   ...
   * });
   */
  /**
   * An Item could be anything, but it needs a property called `label`, which appears in the dropdown.
   * @callback Button#getItems~Callback
   * @param {ButtonScope} scope
   * @since 1.2.0
   * @example
   * TPP_SNAP.registerButton({
   *   label = '',
   *   getItems = () => {
   *     return [
   *       { label: 'Option 1', value: 1 },
   *       { label: 'Option 2', value: 2 },
   *       { label: 'Option 3', value: 3 },
   *     ];
   *   },
   *   execute = (scope, item) => console.log(item.value),
   *   ...
   * });
   */
  /**
   * @callback Button#beforeExecute~Callback
   * @param {ButtonScope} scope
   * @param {any} item - see {@link Button#getItems~Callback}
   * @since 1.2.0
   */
  /**
   * @callback Button#execute~Callback
   * @param {ButtonScope} scope
   * @param {any} item - see {@link Button#getItems~Callback}
   * @return {Promise<void>}
   * @since 1.2.0
   */
  /**
   * @callback Button#afterExecute~Callback
   * @param {ButtonScope} scope
   * @param {any} item - see {@link Button#getItems~Callback}
   * @param {Error} [error] - if an uncatched error appears
   * @since 1.2.0
   */

  /**
   * Define a custom button on the element decoration.
   *
   * @memberof TPP_SNAP
   * @method registerButton
   * @param {Button} button
   * @param {int} [index = -1] - the button index, used as rendering order; `-1` means at the end
   *
   * @since 1.2.0
   *
   * @example
   * // register a debug button, as the first button, on any decorated element
   * TPP_SNAP.registerButton({
   *   css: 'tpp-icon-debug',
   *   execute: async (scope) => console.log(scope),
   * }, 0);
   */
  registerButton({
    label = '',
    css = null,
    icon = null,
    isVisible = (scope) => true,
    isEnabled = async (scope) => false,
    getIcon = async (scope) => (css !== null && !scope.$button.classList.add(css)) || (icon !== null && (scope.$button.style.backgroundImage = `url(${icon})`)) || scope.$button.classList.add('tpp-icon-action'),
    getLabel = async (scope) => label || '',
    getItems = async (scope) => [],
    beforeExecute = async (scope) => {},
    execute = async (scope, item = undefined) => {},
    afterExecute = async (scope) => {},
    _name = 'custom',
  }, index = -1) {
    this._buttons.splice(index < 0 ? this._buttons.length : index, 0, { css, isVisible, isEnabled, getIcon, getLabel, getItems, beforeExecute, execute, afterExecute, _name });
  }

  addButton(...args) {
    this.registerButton(...args);
  }

  /**
   * Override a default button on the element decoration.
   *
   * @memberof TPP_SNAP
   * @method overrideDefaultButton
   * @param {string} defaultButtonName - available button names: `edit`, `metadata`, `add-sibling-section`, `add-child-section-page`, `add-child-section-body`, `workflows`, `delete`, `crop`, `translate`, `move`, and `bookmark`
   * @param {Button|null} buttonOverrides - overrides given methods, `null` removes the default button
   *
   * @since 1.2.0
   *
   * @example
   * // override what the default 'crop' button does
   * TPP_SNAP.overrideDefaultButton('crop', {
   *   execute: async ({ previewId }) => actions.cropImage(previewId, 'my-cropped-resolution'),
   * });
   */
  overrideDefaultButton(name, button) {
    if (button === null) {
      this._buttons = this._buttons.filter(({ _name }) => _name !== name);
    } else {
      const idx = this._buttons.findIndex(({ _name }) => _name === name);
      if (idx !== -1) {
        Object.assign(this._buttons[idx], button);
      }
    }
  }

  /**
   * Find DOM nodes by _PreviewId_.
   *
   * @memberof TPP_SNAP
   * @method findPreviewNodes
   * @param {string} previewId
   * @return {Promise<[...HTMLElement]>} - any DOM node which is associated with the given PreviewId
   *
   * @since 1.2.0
   */
  async findPreviewNodes(previewId) {
    return (await this._actions).findPreviewNodes(...arguments);
  }

  /**
   * Executes a project script or an executable.
   *
   * See also {@link https://docs.e-spirit.com/odfs52/dev/de/espirit/firstspirit/webedit/client/api/Common.html#execute-java.lang.String-JavaScriptObject-JavaScriptObject-|WE_API.Common.execute}.
   *
   * @memberof TPP_SNAP
   * @method execute
   * @param {string} identifier - script ("script:script_uid") or executable ("class:full.qualified.executable.ClassName")
   * @param {object} [params = {}] - parameters (e.g. { param1: 42, param2: 'text' })
   * @param {boolean} [result = true] - should wait for an result
   * @return {Promise<any>} - the result
   *
   * @since 1.2.0
   *
   * @example
   * // https://docs.e-spirit.com/odfs/template-develo/contentcreator/functional-scop/index.html#klassen_navigationbearbeiten
   * TPP_SNAP.execute('class:EditMenu', { node: 43 }).then(() => location.reload());
   */
  async execute(identifier, params = {}, result = true) {
    return (await this._actions).execute(...arguments);
  }

  /**
   * Returns the _PreviewId_ of the ContentCreator scope (see {@link #tpp_snapsetpreviewelement|TPP_SNAP.setPreviewElement}).
   *
   * @memberof TPP_SNAP
   * @method getPreviewElement
   * @return {Promise<string>} - the PreviewId
   *
   * @since 1.2.0
   */
  async getPreviewElement() {
    return (await this._actions).getPreviewElement(...arguments);
  }

  /**
   * Sets the ContentCreator scope to the given _PreviewId_, so e.g. the upper-left colorized
   * _Workflow Status_ is set to the associated FirstSpirit _StoreElement_.
   *
   * @memberof TPP_SNAP
   * @method setPreviewElement
   * @param {string} previewId - the PreviewId
   *
   * @since 1.2.0
   */
  async setPreviewElement(previewId) {
    return (await this._actions).setPreviewElement(...arguments);
  }

  /**
   * Returns the current language abbreviation, set by {@link #tpp_snapsetpreviewelement|TPP_SNAP.setPreviewElement}.
   * Fallback is always `EN`!
   *
   * @memberof TPP_SNAP
   * @method getPreviewLanguage
   * @return {Promise<string>} - the language abbreviation
   *
   * @since 1.2.0
   */
  async getPreviewLanguage() {
    return (await this._actions).getPreviewLanguage(...arguments);
  }

  /**
   * Opens the _Edit Dialog_ of a FirstSpirit _StoreElement_ associated with
   * the _PreviewId_.
   *
   * Triggers {@link onContentChange~Handler}.
   *
   * @memberof TPP_SNAP
   * @method showEditDialog
   * @param {string} previewId - the associated PreviewId
   *
   * @since 1.2.0
   */
  async showEditDialog(previewId) {
    return (await this._actions).showEditDialog(...arguments);
  }

  /**
   * Opens the _Meta Data Dialog_ of an FirtSpirit _StoreElement_ associated with
   * the _PreviewId_.
   * MetaData providing elements must be allowed in the ContentCreator settings, see https://docs.e-spirit.com/odfs/edocs/admi/firstspirit-ser/project-propert/contentcreator/index.html#text_bild_14.
   *
   * Triggers {@link onContentChange~Handler}.
   *
   * @memberof TPP_SNAP
   * @method showMetaDataDialog
   * @param {string} previewId - the associated PreviewId
   *
   * @since NEXT_STABLE
   *
   * @example
   * // display the default button
   * TPP_SNAP.overrideDefaultButton('metadata', {
   *   isVisible: ({ status }) => status.custom === null && (['PageRef', 'Page', 'Section', 'Media'].includes(status.elementType))
   * })
   */
  async showMetaDataDialog(previewId) {
    return (await this._actions).showMetaDataDialog(...arguments);
  }

  /**
   * Fetches the {@link Status} of the given _PreviewId_.
   *
   * @memberof TPP_SNAP
   * @method getElementStatus
   * @param {string} previewId - the associated PreviewId
   * @return {Promise<Status>}
   *
   * @since 1.2.0
   */
  async getElementStatus(previewId) {
    return (await this._actions).getElementStatus(...arguments);
  }

  /**
   * Renders the given _PreviewId_.
   *
   * @memberof TPP_SNAP
   * @method renderElement
   * @param {string} [previewId = null] - the associated PreviewId; if not set, the *StartNode* will be rendered
   * @return {Promise<string|object>} - the rendering result of the FirstSpirit template; if the result is a JSON, the JSON will automatically parsed
   *
   * @since 1.2.0
   */
  async renderElement(previewId = null) {
    return (await this._actions).renderElement(...arguments);
  }

  /**
   * Tries to delete a FirstSpirit _StoreElement_ based on the _PreviewId_.
   *
   * Triggers {@link onContentChange~Handler}.
   *
   * You can also delete elements by using a specific Workflow - see {@link #tpp_snapstartworkflow|TPP_SNAP.startWorkflow}.
   *
   * @memberof TPP_SNAP
   * @method deleteElement
   * @param {string} previewId - the associated PreviewId
   * @param {boolean} showConfirmDialog - if true, the user will be asked to confirm the deletion before it is performed
   *
   * @since 1.2.0
   */
  async deleteElement(previewId, showConfirmDialog = false) {
    return (await this._actions).deleteElement(...arguments);
  }

  /**
   * Starts a FirstSpirit _Workflow_ on the given _PreviewId_.
   *
   * @memberof TPP_SNAP
   * @method startWorkflow
   * @param {string} previewId - the associated PreviewId
   * @param {string} workflow - a workflow uid, can be found in {@link Status}
   *
   * @since 1.2.0
   */
  async startWorkflow(previewId, workflow) {
    return (await this._actions).startWorkflow(...arguments);
  }

  /**
   * Processes a _Workflow_ transition.
   *
   * @memberof TPP_SNAP
   * @method processWorkflow
   * @param {string} previewId - the associated PreviewId
   * @param {string} transition - a transition uid, can be found in {@link Status}
   *
   * @since 1.2.0
   */
  async processWorkflow(previewId, transition) {
    return (await this._actions).processWorkflow(...arguments);
  }

  /**
   * Creates a new _Page_ (and _PageRef_) in FirstSpirit.
   *
   * Triggers {@link onRerenderView~Handler} and {@link onNavigationChange~Handler}.
   *
   * @memberof TPP_SNAP
   * @method createPage
   * @param {string} path - the (uid-)path in FirstSpirit's PageStore/SiteStore (separated by '/')
   * @param {string} uid - the name/uid for the new page
   * @param {string} template - the PageTemplate uid
   * @param {object} [options]
   * @param {string} [options.language = null] - a specific language, `null` means the current language
   * @param {boolean} [options.result = false] - if `true`, the {@link onRerenderView~Handler} will **not** be triggered and the
   *   {@link #tpp_snaprenderelement|TPP_SNAP.renderElement} result will be returned.
   *
   *   **Be careful when preventing the *change event*!
   *   The affected PreviewId could appear several times in the DOM!**
   * @param {boolean} [options.showFormDialog = true] - if `true`, the page form will be shown
   * @since 1.2.0
   */
  async createPage(path, uid, template, { language = null, showFormDialog = true } = {}) {
    return (await this._actions).createPage(...arguments);
  }

  /**
   * Adds a new _Section_.
   *
   * Triggers {@link onContentChange~Handler} if _PreviewId_ fulfills a _Page_ or
   * _PageRef_, otherwise {@link onRerenderView~Handler} would directly be triggered.
   *
   * @memberof TPP_SNAP
   * @method createSection
   * @param {string} previewId - the PreviewId of a parent or sibling element
   * @param {object} [options]
   * @param {string} [options.template = null] - a _SectionTemplate_ uid; if not set a selection overlay will displayed
   * @param {string} [options.name = null] - the name of the _Section_
   * @param {int} [options.index = null] - the index where the section should be inserted; default is last position
   * @param {boolean} [options.result = false] - if `true`, the {@link onContentChange~Handler}/{@link onRerenderView~Handler} will **not** be triggered and the
   *   {@link #tpp_snaprenderelement|TPP_SNAP.renderElement} result will be returned.
   *
   *   **Be careful when preventing the *change event*!
   *   The affected PreviewId could appear several times in the DOM!**
   *
   * @since 1.2.0
   */
  async createSection(previewId, { template = null, name = null, index = null } = {}) {
    return (await this._actions).createSection(...arguments);
  }

  /**
   * Move a _Section_ before or after another _Section_.
   *
   * Triggers {@link onRerenderView~Handler}.
   *
   * @memberof TPP_SNAP
   * @method moveSection
   * @param {string} source - the PreviewId of a the source _Section_
   * @param {string} target - the PreviewId of a the target _Section_, _Body_ or _Page_ with a single _Body_
   * @param {object} [options]
   * @param {boolean} [options.before = false] - (only relevant if target is a _Section_) move the source _Section_ before target _Section_, otherwise it would be moved after
   * @param {boolean} [options.copy = false] - create a copy of the source _Section_
   * @param {boolean} [options.skipRerenderEvent = false] - if `true`, the {@link onRerenderView~Handler} will **not** be triggered
   *
   *   **Be careful when preventing the *change event*!
   *   The affected PreviewId could appear several times in the DOM!**
   *
   * @return true, if the operation was successful, false otherwise
   * @since 1.2.4
   */
  async moveSection(source, target, { before = false, copy = false, skipRerenderEvent = false }) {
    // Element retrieved here apparently out of IdProviderInfo
    let { elementType: sourceElementType, id: sourceId } = await this.getElementStatus(source);
    if (sourceElementType !== 'Section') {
      console.error('[moveSection] Given "source" must be a Section!', { previewId: source });
      return false;
    }

    let { elementType: targetElementType, id: targetId } = await this.getElementStatus(target);
    if (!['Page', 'Body', 'Section'].includes(targetElementType)) {
      console.error('[moveSection] Given "target" must be a Page, Body or Section!', { previewId: target });
      return false;
    }

    return (await this._actions)._transferSection(sourceId, targetId, { position: before ? 'BEFORE' : 'AFTER', mode: copy ? 'COPY' : 'MOVE', skipRerenderEvent });
  }

  /**
   * Creates a new _Dataset_.
   *
   * Triggers {@link onRerenderView~Handler}.
   *
   * @memberof TPP_SNAP
   * @method createDataset
   * @param {string} template - the TableTemplate uid
   * @param {object} [options]
   * @param {string} [options.language = null] - a specific language, `null` means the current language
   * @param {boolean} [options.result = false] - if `true`, the {@link onRerenderView~Handler} will **not** be triggered and the
   *   {@link #tpp_snaprenderelement|TPP_SNAP.renderElement} result will be returned.
   *
   *   **Be careful when preventing the *change event*!
   *   The affected PreviewId could appear several times in the DOM!**
   *
   * @since 1.2.0
   */
  async createDataset(template, { language = null } = {}) {
    return (await this._actions).createDataset(...arguments);
  }

  /**
   * Toggles an element to be bookmarked (flagged as master) or not. Bookmarked elements will appear
   * in creation dialogs, like {@link #tpp_snapcreatesection|TPP_SNAP.createSection}.
   *
   * @memberof TPP_SNAP
   * @method toggleBookmark
   * @param {string} previewId
   *
   * @since 1.2.0
   */
  async toggleBookmark(previewId) {
    return (await this._actions).toggleBookmark(...arguments);
  }

  /**
   * Shows the _Crop Dialog_ for an image.
   *
   * Triggers {@link onContentChange~Handler}, the `content` parameter contains the URL in this case.
   *
   * @memberof TPP_SNAP
   * @method cropImage
   * @param {string} previewId - a PreviewId of _Media_ (type _Picture_)
   * @param {string|Array.<string>} [resolution = 'ORIGINAL'] - the uid of a _Resolution_ / array of uids
   * @param {boolean} [result = false] - if `true`, the {@link onContentChange~Handler} will **not** be triggered and the URL will be returned.
   *
   *   **Be careful when preventing the *change event*!
   *   The affected PreviewId could appear several times in the DOM!**
   *
   * @since 1.2.0
   */
  async cropImage(previewId, resolution = 'ORIGINAL') {
    return (await this._actions).cropImage(...arguments);
  }

  /**
   * Returns all project languages (abbreviations). The first language is always the master language.
   *
   * @memberof TPP_SNAP
   * @method languages
   * @return {Promise<Array.<string>>} - list of languages
   *
   * @since 1.2.4
   */
  async languages() {
    return (await this._actions).languages(...arguments);
  }

  /**
   * Returns the "External Preview URL" as set in FirstSpirit.
   *
   * @memberof TPP_SNAP
   * @method previewUrl
   * @return {Promise<string>} - the "External Preview URL"
   *
   * @since 1.2.4
   */
  async previewUrl() {
    return (await this._actions).previewUrl(...arguments);
  }

  /**
   * Shows the translation help dialogue.
   *
   * Triggers {@link onContentChange~Handler} if the current language is the same
   * as the given `target` language.
   *
   * @memberof TPP_SNAP
   * @method showTranslationDialog
   * @param {string} previewId - a PreviewId of DataProvider
   * @param {string} source - the source language
   * @param {string} target - the target language
   *
   * @since 1.2.4
   */
  async showTranslationDialog() {
    return (await this._actions).showTranslationDialog(...arguments);
  }

  /**
   * Trigger {@link onContentChange~Handler}. Can be used if a _Custom Button_
   * changes the content.
   *
   * @memberof TPP_SNAP
   * @method triggerChange
   * @param {string} previewId - the target PreviewId
   * @param {string|object} [content = null] - the updated content, if `null` {@link #tpp_snaprenderelement|TPP_SNAP.renderElement} is called internally
   *
   * @since 1.2.0
   */
  async triggerChange(previewId, content = null) {
    return (await this._actions).triggerChange(...arguments);
  }

  /**
   * Trigger {@link TPP_SNAP.triggerRerenderView}.
   *
   * @memberof TPP_SNAP
   * @method triggerRerenderView
   *
   * @since 1.2.0
   */
  async triggerRerenderView() {
    return (await this._actions).triggerRerenderView(...arguments);
  }

  /**
   * Async wrapper for {@link https://docs.e-spirit.com/odfs/dev/de/espirit/firstspirit/client/mpp/MPPWebControl.html#getParameter-java.lang.String-|MPP_API.getParameter}.
   *
   * @memberof TPP_SNAP
   * @method mppGetParameter
   * @param {string} name
   * @return {Promise<object>}
   *
   * @since 1.2.0
   */
  async mppGetParameter(name) {
    return (await this._actions).mppGetParameter(...arguments);
  }

  /**
   * Async wrapper for {@link https://docs.e-spirit.com/odfs/dev/de/espirit/firstspirit/client/mpp/MPPWebControl.html#getTimeParameter--|MPP_API.getTimeParameter}.
   *
   * @memberof TPP_SNAP
   * @method mppGetTimeParameter
   * @return {Promise<Date>}
   *
   * @since 1.2.0
   */
  async mppGetTimeParameter() {
    return (await this._actions).mppGetTimeParameter(...arguments);
  }

  /**
   * Async wrapper for {@link https://docs.e-spirit.com/odfs/dev/de/espirit/firstspirit/client/mpp/MPPWebControl.html#isParameterized--|MPP_API.isParameterized}.
   *
   * @memberof TPP_SNAP
   * @method mppIsParameterized
   * @return {Promise<boolean>}
   *
   * @since 1.2.0
   */
  async mppIsParameterized() {
    return (await this._actions).mppIsParameterized(...arguments);
  }

  /**
   * Async wrapper for {@link https://docs.e-spirit.com/odfs/dev/de/espirit/firstspirit/client/mpp/MPPWebControl.html#setParameter-java.lang.String-java.lang.Object-|MPP_API.setParameter}.
   *
   * @memberof TPP_SNAP
   * @method mppSetParameter
   * @param {string} name
   * @param {object} value
   *
   * @since 1.2.0
   */
  async mppSetParameter(name, value) {
    return (await this._actions).mppSetParameter(...arguments);
  }

  /**
   * Async wrapper for {@link https://docs.e-spirit.com/odfs/dev/de/espirit/firstspirit/client/mpp/MPPWebControl.html#setTimeParameter-java.lang.Object-|MPP_API.setTimeParameter}.
   *
   * @memberof TPP_SNAP
   * @method mppSetTimeParameter
   * @param {Date} date
   *
   * @since 1.2.0
   */
  async mppSetTimeParameter(date) {
    return (await this._actions).mppSetTimeParameter(...arguments);
  }

  /**
   * The status is not a stable API, yet.
   *
   * @name Status
   * @param {string} previewId - the raw PreviewId
   * @param {string|null} custom - is null, if the status is delivered by FirstSpirit. Otherwise this contains information from the markup.
   */

  /**
   * A custom PreviewId starts with `custom:` and can be used in a URN Schema way to create custom buttons.
   *
   * @name CustomStatus
   * @extends Status
   * @param {array[...string]} parts - splitted (delimiter: `:`) version of `custom`
   *
   * @since 1.2.0
   *
   * @example
   * // <div data-preview-id="custom:create-page:my/firstspirit/path:myPageName"></div>
   *
   * TPP_SNAP.registerButton({
   *   css: 'tpp-icon-create-page',
   *   label: 'addMyPageName',
   *   isVisible: ({ status }) => status.custom !== null && status.parts[0] === 'create-page',
   *   execute: ({ status }) => {
   *     const [, path, name] = status.parts;
   *     TPP_SNAP.createPage(path, name, 'page_template_uid');
   *   },
   * })
   */
}


let _actionsPromiseResolver, _actionsPromise = new Promise(resolve => _actionsPromiseResolver = resolve);
const events = new $EventEmitter();
let _connectedPromise = new Promise(resolve => events.on(Event.Initialized, (connected) => resolve(connected)));

const preview = new FirstSpiritPreview({ actions: _actionsPromise, connected: _connectedPromise });
export default preview;

if (typeof(window) !== 'undefined' && top !== self) {
  const targetOrigin = $('[data-firstspirit-origin]:not([data-firstspirit-origin=""])') && $('[data-firstspirit-origin]:not([data-firstspirit-origin=""])').dataset.firstspiritOrigin || '*';
  const messenger = new Messenger({ events, targetOrigin });
  const actions = new Actions({ events, messenger });
  _actionsPromiseResolver(actions);
  Decoration({ events, messenger, actions, preview });

  const trigger = async (eventName, ...args) => {
    const listener = preview._listener.filter(([name]) => eventName === name);
    if (listener.length > 0) {
      return await Promise.all(listener.map(([, fn]) => fn(...args)));
    } else {
      eventName = `${eventName}Fallback`;
      return await Promise.all(preview._listener.filter(([name]) => eventName === name).map(([, fn]) => fn(...args)));
    }
    return [];
  };

  events.on(Event.RerenderView, () => trigger('onRerenderView'));
  events.on(Event.PreviewRequest, (previewId) => trigger('onRequestPreviewElement', previewId));
  events.on(Event.NavigationChange, (previewId) => trigger('onNavigationChange', previewId));
  events.on(Event.ElementChange, async ({ previewId, content }) => {
    if (typeof content === 'undefined') {
      content = await actions.renderElement(previewId);
    }
    const captures = [].concat(...(await Promise.all(actions.findPreviewNodes(previewId, true).map($node => trigger('onContentChange', $node, previewId, content)))));
    if (!captures.some(result => typeof result !== 'undefined')) {
      trigger('onRerenderView');
    }
  });

  const context = { events, messenger, actions, preview };

  DefaultButtons(context);
  DragAndDrop(context);
  DisplayChanges(context);

} else {
  events.emit(Event.Initialized, false);
}
